/**
 * FrsModal — a non-intrusive, read-only viewer for a project's Functional
 * Requirements Specification (FRS), rendered as markdown.
 *
 * Used from Builder Mode so the user can glance at what the AI understood it
 * should build without leaving the live preview + chat. Opens over the page
 * and dismisses with Escape / backdrop / close button.
 */

import { useEffect, useState } from 'react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { Loader2, FileText } from 'lucide-react'
import Modal from './Modal'
import { getFrsContent } from '@/api/generateapp'

interface FrsModalProps {
  open: boolean
  project: string
  onClose: () => void
}

export default function FrsModal({ open, project, onClose }: FrsModalProps) {
  const [content, setContent] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!open || !project) return
    let cancelled = false
    setLoading(true)
    setError(null)
    setContent(null)
    getFrsContent(project)
      .then((res: { content?: string }) => {
        if (!cancelled) setContent(res?.content ?? '')
      })
      .catch((err: unknown) => {
        if (cancelled) return
        const status = (err as { response?: { status?: number } })?.response?.status
        setError(
          status === 404
            ? "The functional spec hasn't been generated yet. It'll appear here once the AI finishes the first build."
            : 'Could not load the functional spec. Please try again.',
        )
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [open, project])

  return (
    <Modal open={open} onClose={onClose} title="Functional Specification" size="full">
      <div style={{ minHeight: 220, maxHeight: '70vh', overflowY: 'auto' }}>
        {loading && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 8,
              padding: '3rem 0',
              color: 'var(--color-text-secondary)',
              fontSize: '0.9375rem',
            }}
          >
            <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} />
            Loading specification…
          </div>
        )}

        {!loading && error && (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 10,
              padding: '3rem 1rem',
              textAlign: 'center',
              color: 'var(--color-text-secondary)',
            }}
          >
            <FileText size={28} style={{ opacity: 0.3 }} />
            <p style={{ margin: 0, fontSize: '0.9375rem', maxWidth: 420, lineHeight: 1.5 }}>
              {error}
            </p>
          </div>
        )}

        {!loading && !error && content !== null && (
          content.trim() ? (
            <div className="markdown-content" style={{ fontSize: '0.9375rem', lineHeight: 1.6 }}>
              <ReactMarkdown remarkPlugins={[remarkGfm]}>{content}</ReactMarkdown>
            </div>
          ) : (
            <div
              style={{
                padding: '3rem 1rem',
                textAlign: 'center',
                color: 'var(--color-text-secondary)',
                fontSize: '0.9375rem',
              }}
            >
              The specification is empty.
            </div>
          )
        )}
      </div>
    </Modal>
  )
}
